import React, { useState, useRef } from 'react';
import { Settings, Download, Upload, Plus, Minus, Play, Save, Pause, Volume2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import FrequencySlider, { getFrequencyDescription } from './FrequencySlider';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import { BinauralBeatGenerator } from '@/utils/audioUtils';

export interface SessionStep {
  id: string;
  frequency: number;
  duration: number; // in minutes
  volume?: number;
  description?: string;
  color?: string;
  leftWaveform?: string;
  rightWaveform?: string;
}

export interface SessionConfig {
  name: string;
  description: string;
  steps: SessionStep[];
  totalDuration: number; // calculated field
  createdAt: string;
  updatedAt: string;
  version: string;
}

interface AdvancedSessionProps {
  onCreateSession: (steps: SessionStep[]) => void;
}

const AdvancedSession: React.FC<AdvancedSessionProps> = ({ onCreateSession }) => {
  const [sessionConfig, setSessionConfig] = useState<SessionConfig>({
    name: "New Custom Session",
    description: "Custom multi-frequency binaural beats session",
    steps: [
      { id: '1', frequency: 10, duration: 5, volume: 100, color: '#5B6AFE' }
    ],
    totalDuration: 5,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: "1.0.0"
  });
  
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<SessionStep | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  const [previewingStepId, setPreviewingStepId] = useState<string | null>(null);
  const previewGeneratorRef = useRef<BinauralBeatGenerator | null>(null);
  
  const form = useForm({
    defaultValues: {
      name: sessionConfig.name,
      description: sessionConfig.description
    }
  });
  
  React.useEffect(() => {
    previewGeneratorRef.current = new BinauralBeatGenerator();
    
    return () => {
      if (previewGeneratorRef.current) {
        previewGeneratorRef.current.stop();
      }
    };
  }, []);
  
  const calculateTotalDuration = (steps: SessionStep[]): number => {
    return steps.reduce((total, step) => total + step.duration, 0);
  };
  
  const handleAddStep = () => {
    const newId = (sessionConfig.steps.length + 1).toString();
    const lastStep = sessionConfig.steps[sessionConfig.steps.length - 1];
    
    const hue = Math.floor(Math.random() * 60) + 220; // blues/purples
    const saturation = Math.floor(Math.random() * 30) + 70;
    const lightness = Math.floor(Math.random() * 20) + 60;
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    
    const newStep: SessionStep = {
      id: newId,
      frequency: lastStep ? lastStep.frequency : 10,
      duration: 5,
      volume: 100,
      color
    };
    
    const updatedSteps = [...sessionConfig.steps, newStep];
    const totalDuration = calculateTotalDuration(updatedSteps);
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      totalDuration,
      updatedAt: new Date().toISOString()
    });
  };
  
  const handleRemoveStep = (id: string) => {
    if (sessionConfig.steps.length <= 1) {
      toast.error("Cannot remove the only step", {
        description: "Your session must have at least one step"
      });
      return;
    }
    
    const updatedSteps = sessionConfig.steps.filter(step => step.id !== id);
    const totalDuration = calculateTotalDuration(updatedSteps);
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      totalDuration,
      updatedAt: new Date().toISOString()
    });
  };
  
  const handleUpdateStep = (updatedStep: SessionStep) => {
    const updatedSteps = sessionConfig.steps.map(step => 
      step.id === updatedStep.id ? updatedStep : step
    );
    
    const totalDuration = calculateTotalDuration(updatedSteps);
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      totalDuration,
      updatedAt: new Date().toISOString()
    });
    
    setEditingStep(null);
    setIsEditDialogOpen(false);
  };
  
  const handleEditStep = (step: SessionStep) => {
    setEditingStep({...step});
    setIsEditDialogOpen(true);
  };
  
  const handleFrequencyChange = (stepId: string, value: number) => {
    const updatedSteps = sessionConfig.steps.map(step => 
      step.id === stepId ? { ...step, frequency: value } : step
    );
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      updatedAt: new Date().toISOString()
    });
  };
  
  const handleDurationChange = (stepId: string, value: number) => {
    const updatedSteps = sessionConfig.steps.map(step => 
      step.id === stepId ? { ...step, duration: value } : step
    );
    
    const totalDuration = calculateTotalDuration(updatedSteps);
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      totalDuration,
      updatedAt: new Date().toISOString()
    });
  };
  
  const handleVolumeChange = (stepId: string, value: number) => {
    const updatedSteps = sessionConfig.steps.map(step => 
      step.id === stepId ? { ...step, volume: value } : step
    );
    
    setSessionConfig({
      ...sessionConfig,
      steps: updatedSteps,
      updatedAt: new Date().toISOString()
    });
    
    if (previewingStepId === stepId && previewGeneratorRef.current) {
      previewGeneratorRef.current.setVolume(value);
    }
  };
  
  const handleExportConfig = () => {
    const configJSON = JSON.stringify(sessionConfig, null, 2);
    const blob = new Blob([configJSON], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const sanitizedName = sessionConfig.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    
    a.href = url;
    a.download = `binauralflow_${sanitizedName}_${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setIsExportDialogOpen(false);
    toast.success("Configuration exported", {
      description: "Your session configuration has been downloaded"
    });
  };
  
  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const imported: SessionConfig = JSON.parse(content);
        
        if (!imported.steps || !Array.isArray(imported.steps) || imported.steps.length === 0) {
          throw new Error("Invalid session configuration: missing or empty steps array");
        }
        
        for (const step of imported.steps) {
          if (typeof step.frequency !== 'number' || typeof step.duration !== 'number' || typeof step.volume !== 'number') {
            throw new Error("Invalid step: frequency, duration, and volume must be numbers");
          }
        }
        
        const now = new Date().toISOString();
        
        setSessionConfig({
          ...imported,
          totalDuration: calculateTotalDuration(imported.steps),
          updatedAt: now
        });
        
        toast.success("Configuration imported", {
          description: `Loaded "${imported.name}" with ${imported.steps.length} steps`
        });
      } catch (error) {
        console.error("Error importing configuration:", error);
        toast.error("Import failed", {
          description: "The selected file is not a valid session configuration"
        });
      }
    };
    
    reader.readAsText(file);
    event.target.value = '';
  };
  
  const handleStartSession = () => {
    onCreateSession(sessionConfig.steps);
    toast.success("Advanced session started", {
      description: `Playing ${sessionConfig.name} with ${sessionConfig.steps.length} steps`
    });
  };
  
  const handleUpdateConfig = (data: { name: string; description: string }) => {
    setSessionConfig({
      ...sessionConfig,
      name: data.name,
      description: data.description,
      updatedAt: new Date().toISOString()
    });
    
    setIsExportDialogOpen(false);
    toast.success("Session details updated", {
      description: "Your session configuration has been updated"
    });
  };
  
  const handlePreviewToggle = (stepId: string, frequency: number, volume: number) => {
    if (previewingStepId === stepId) {
      if (previewGeneratorRef.current) {
        previewGeneratorRef.current.stop();
      }
      setPreviewingStepId(null);
    } else {
      if (previewGeneratorRef.current) {
        previewGeneratorRef.current.stop();
      }
      
      if (previewGeneratorRef.current) {
        previewGeneratorRef.current.play(frequency);
        previewGeneratorRef.current.setVolume(volume);
      }
      setPreviewingStepId(stepId);
      
      toast.info(`Previewing ${frequency.toFixed(1)} Hz at ${volume}% volume`, {
        description: getFrequencyDescription(frequency),
        duration: 3000
      });
    }
  };
  
  const getBrainwaveCategory = (frequency: number): string => {
    const description = getFrequencyDescription(frequency).split(" - ")[0];
    return description;
  };
  
  return (
    <div className="glass rounded-2xl p-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Multi-Stage Session Designer</h3>
        <div className="flex items-center gap-2">
          <label htmlFor="import-config" className="cursor-pointer">
            <div className="flex items-center gap-1 text-sm text-primary p-1 rounded-md hover:bg-primary/10">
              <Upload size={16} />
              <span className="sr-only md:not-sr-only">Import</span>
            </div>
            <input 
              id="import-config" 
              type="file" 
              accept=".json" 
              className="hidden" 
              onChange={handleImportConfig}
            />
          </label>
          <div 
            className="flex items-center gap-1 text-sm text-primary p-1 rounded-md hover:bg-primary/10 cursor-pointer"
            onClick={() => setIsExportDialogOpen(true)}
          >
            <Download size={16} />
            <span className="sr-only md:not-sr-only">Export</span>
          </div>
        </div>
      </div>
      
      <div className="mb-4 p-3 bg-card/50 rounded-lg border border-border/50">
        <h4 className="text-sm font-medium mb-1">{sessionConfig.name}</h4>
        <p className="text-xs text-muted-foreground mb-2">{sessionConfig.description}</p>
        <div className="flex items-center justify-between text-xs">
          <span>Total Duration: {sessionConfig.totalDuration} minutes</span>
          <span>Steps: {sessionConfig.steps.length}</span>
        </div>
      </div>
      
      <div className="flex flex-col gap-6 mb-6">
        {sessionConfig.steps.map((step, index) => (
          <div 
            key={step.id} 
            className="relative p-4 border border-border/50 rounded-lg bg-background/50 transition-all hover:bg-background"
            style={{ 
              borderLeft: `4px solid ${step.color || '#5B6AFE'}`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-medium">
                Step {index + 1}: {step.frequency.toFixed(1)} Hz <span className="text-xs text-muted-foreground">({getBrainwaveCategory(step.frequency)})</span>
              </h5>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={cn(
                    "h-7 w-7 p-0", 
                    previewingStepId === step.id && "bg-primary/20 text-primary"
                  )} 
                  onClick={() => handlePreviewToggle(step.id, step.frequency, step.volume || 100)}
                  title={previewingStepId === step.id ? "Stop Preview" : "Preview Sound"}
                >
                  {previewingStepId === step.id ? (
                    <Pause size={14} />
                  ) : (
                    <Play size={14} />
                  )}
                  <span className="sr-only">
                    {previewingStepId === step.id ? "Stop Preview" : "Preview Sound"}
                  </span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0" 
                  onClick={() => handleEditStep(step)}
                >
                  <Settings size={14} />
                  <span className="sr-only">Edit</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0 text-destructive" 
                  onClick={() => handleRemoveStep(step.id)}
                >
                  <Minus size={14} />
                  <span className="sr-only">Remove</span>
                </Button>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Frequency</span>
                <span className="text-xs font-mono">{step.frequency.toFixed(1)} Hz</span>
              </div>
              <Slider
                value={[step.frequency]}
                min={0.5}
                max={40}
                step={0.5}
                onValueChange={(values) => handleFrequencyChange(step.id, values[0])}
              />
            </div>
            
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Duration</span>
                <span className="text-xs font-mono">{step.duration} min</span>
              </div>
              <Slider
                value={[step.duration]}
                min={1}
                max={30}
                step={1}
                onValueChange={(values) => handleDurationChange(step.id, values[0])}
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium flex items-center gap-1">
                  <Volume2 size={12} />
                  Volume
                </span>
                <span className="text-xs font-mono">{step.volume || 100}%</span>
              </div>
              <Slider
                value={[step.volume || 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(values) => handleVolumeChange(step.id, values[0])}
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex flex-col gap-3">
        <Button 
          variant="outline" 
          onClick={handleAddStep}
          className="border-dashed border-primary/50 text-primary"
        >
          <Plus size={16} className="mr-2" />
          Add Step
        </Button>
        
        <Button 
          onClick={handleStartSession}
          className="gap-2"
        >
          <Play size={16} />
          Start Custom Session
        </Button>
      </div>
      
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open && previewingStepId) {
          if (previewGeneratorRef.current) {
            previewGeneratorRef.current.stop();
          }
          setPreviewingStepId(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Step</DialogTitle>
            <DialogDescription>
              Fine-tune the parameters for this session step.
            </DialogDescription>
          </DialogHeader>
          
          {editingStep && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="step-freq">
                  Frequency (Hz) 
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({getBrainwaveCategory(editingStep.frequency)})
                  </span>
                </Label>
                <div className="flex items-center gap-2">
                  <FrequencySlider
                    value={editingStep.frequency}
                    onChange={(value) => setEditingStep({...editingStep, frequency: value})}
                    min={0.5}
                    max={40}
                    step={0.1}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-8 w-8 flex-shrink-0",
                      previewingStepId === editingStep.id && "bg-primary/20 text-primary"
                    )}
                    onClick={() => handlePreviewToggle(editingStep.id, editingStep.frequency, editingStep.volume || 100)}
                  >
                    {previewingStepId === editingStep.id ? (
                      <Pause size={14} />
                    ) : (
                      <Play size={14} />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="step-duration">Duration (minutes)</Label>
                <Input
                  id="step-duration"
                  type="number"
                  min={1}
                  max={60}
                  value={editingStep.duration}
                  onChange={(e) => setEditingStep({
                    ...editingStep, 
                    duration: Number(e.target.value)
                  })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="step-volume" className="flex items-center gap-1">
                  <Volume2 size={14} />
                  Volume
                </Label>
                <div className="flex items-center gap-2">
                  <Slider
                    id="step-volume"
                    value={[editingStep.volume || 100]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={(values) => setEditingStep({
                      ...editingStep,
                      volume: values[0]
                    })}
                    className="flex-1"
                  />
                  <span className="text-sm font-mono w-10 text-right">{editingStep.volume || 100}%</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="step-description">Description (optional)</Label>
                <Input
                  id="step-description"
                  value={editingStep.description || ''}
                  onChange={(e) => setEditingStep({
                    ...editingStep, 
                    description: e.target.value
                  })}
                  placeholder="e.g., Deep relaxation phase"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="step-color">Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="step-color"
                    value={editingStep.color || '#5B6AFE'}
                    onChange={(e) => setEditingStep({
                      ...editingStep,
                      color: e.target.value
                    })}
                    className="h-10 w-10 rounded border cursor-pointer"
                  />
                  <span className="text-sm">{editingStep.color || '#5B6AFE'}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setEditingStep(null);
                setIsEditDialogOpen(false);
                if (previewingStepId && previewGeneratorRef.current) {
                  previewGeneratorRef.current.stop();
                  setPreviewingStepId(null);
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (previewingStepId && previewGeneratorRef.current) {
                  previewGeneratorRef.current.stop();
                  setPreviewingStepId(null);
                }
                editingStep && handleUpdateStep(editingStep);
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isExportDialogOpen} onOpenChange={(open) => {
        setIsExportDialogOpen(open);
        if (!open && previewingStepId) {
          if (previewGeneratorRef.current) {
            previewGeneratorRef.current.stop();
          }
          setPreviewingStepId(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Session</DialogTitle>
            <DialogDescription>
              Customize session details before exporting
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form 
              onSubmit={form.handleSubmit(handleUpdateConfig)} 
              className="space-y-4 py-2"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="My Custom Session" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="A brief description of this session" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="pt-4 flex justify-between">
                <Button 
                  type="submit"
                  variant="outline"
                >
                  <Save size={16} className="mr-2" />
                  Save Details
                </Button>
                
                <Button 
                  type="button"
                  onClick={handleExportConfig}
                >
                  <Download size={16} className="mr-2" />
                  Export JSON
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancedSession;
